/**
 * Transient one-line feedback.
 *
 * For outcomes the world cannot show by itself — "till the soil first",
 * "your bag is full". Anything visible in the world (soil turning dark, a crop
 * disappearing into the bag) deliberately gets no toast, because narrating what
 * the player can already see trains them to ignore the messages that matter.
 */

const VISIBLE_MS = 1800;

export class Toast {
  private readonly root: HTMLDivElement;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastMessage = '';

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'toast';
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');
    container.appendChild(this.root);
  }

  show(message: string): void {
    // Re-showing the same message restarts the timer rather than stacking, so
    // mashing a button against a wall does not build a queue.
    this.root.textContent = message;
    this.root.classList.add('is-showing');
    this.lastMessage = message;

    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.root.classList.remove('is-showing');
      this.timer = null;
    }, VISIBLE_MS);
  }

  /** Current message, for tests. */
  get message(): string {
    return this.lastMessage;
  }

  destroy(): void {
    if (this.timer) clearTimeout(this.timer);
    this.root.remove();
  }
}
